import React, { useState } from "react";
import { toast } from "sonner";
import { X, ChevronRight, ChevronLeft, Check } from "lucide-react";
import { api, formatErr } from "@/lib/api";

const CATEGORIES = ["Group Tours", "Customized Tours"];

export default function InquiryModal({ open, onClose, tourId, tourTitle, tourCategory, departureDates = [] }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    destinationInterest: "",
    travelerCount: 2,
    preferredDepartureDate: "",
    pace: "Relaxed",
    customerName: "",
    email: "",
    phone: "",
    specialRequirements: "",
  });
  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const notes = `${form.destinationInterest ? "Destination interest: " + form.destinationInterest + ". " : ""}Pace: ${form.pace}. ${form.specialRequirements || ""}`.trim();
      await api.post("/inquiries", {
        customerName: form.customerName,
        email: form.email,
        phone: form.phone,
        tourId: tourId || null,
        travelerCount: Number(form.travelerCount) || 1,
        preferredDepartureDate: form.preferredDepartureDate || null,
        specialRequirements: notes,
      });
      toast.success("Inquiry sent! Our team will reach out within 24 hours.");
      onClose();
      setStep(1);
      setForm({ destinationInterest: "", travelerCount: 2, preferredDepartureDate: "", pace: "Relaxed",
        customerName: "", email: "", phone: "", specialRequirements: "" });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm p-4 flex items-center justify-center" onClick={onClose} data-testid="inquiry-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl slide-in-right" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-amber-900/10">
          <div>
            <div className="text-xs text-amber-800 font-semibold tracking-widest uppercase">Step {step} of 3</div>
            <h3 className="font-display text-2xl font-bold text-maroon">
              {tourTitle ? `Enquire — ${tourTitle}` : "Customize My Trip"}
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-maroon" data-testid="inquiry-close-btn"><X /></button>
        </div>
        <div className="p-6 space-y-4">
          {step === 1 && (
            <div className="space-y-4">
              {!tourId && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Destination interest</label>
                  <input value={form.destinationInterest} onChange={(e)=>set("destinationInterest", e.target.value)}
                    placeholder="e.g. Switzerland, Kerala, Mediterranean cruise…"
                    className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base"
                    data-testid="inquiry-destination" />
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Number of travellers</label>
                  <input type="number" min={1} max={40} value={form.travelerCount} onChange={(e)=>set("travelerCount", e.target.value)}
                    className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base"
                    data-testid="inquiry-travelers" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Preferred departure</label>
                  {tourCategory === "Group Tours" && departureDates.length > 0 ? (
                    <select value={form.preferredDepartureDate} onChange={(e)=>set("preferredDepartureDate", e.target.value)}
                      className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base"
                      data-testid="inquiry-date">
                      <option value="">Select a departure date</option>
                      {departureDates.map((d) => (
                        <option key={d} value={d}>
                          {new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input type="date" value={form.preferredDepartureDate} onChange={(e)=>set("preferredDepartureDate", e.target.value)}
                      className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base"
                      data-testid="inquiry-date" />
                  )}
                </div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-slate-700 mb-2">Preferred pace</label>
              <div className="grid sm:grid-cols-3 gap-3">
                {["Relaxed","Balanced","Adventurous"].map((p) => (
                  <button key={p} onClick={()=>set("pace", p)} type="button"
                    className={`h-14 rounded-lg border-2 font-semibold text-base transition ${form.pace===p ? "border-amber-600 bg-amber-50 text-amber-800" : "border-amber-900/15 text-slate-700 hover:border-amber-400"}`}
                    data-testid={`inquiry-pace-${p.toLowerCase()}`}>
                    {form.pace===p && <Check className="inline mr-1" size={16} />}{p}
                  </button>
                ))}
              </div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 mt-4">Any special requirements?</label>
              <textarea rows={4} value={form.specialRequirements} onChange={(e)=>set("specialRequirements", e.target.value)}
                placeholder="Dietary needs, mobility considerations, room preferences…"
                className="w-full px-4 py-3 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base"
                data-testid="inquiry-notes" />
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                <input value={form.customerName} onChange={(e)=>set("customerName", e.target.value)}
                  className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base"
                  data-testid="inquiry-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Email *</label>
                  <input type="email" value={form.email} onChange={(e)=>set("email", e.target.value)}
                    className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base"
                    data-testid="inquiry-email" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Phone *</label>
                  <input value={form.phone} onChange={(e)=>set("phone", e.target.value)}
                    className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base"
                    data-testid="inquiry-phone" />
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-2">By submitting, you agree we may contact you via phone or email. We never share your details.</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 p-6 border-t border-amber-900/10 bg-warm-surface/60">
          <button onClick={()=>step>1?setStep(step-1):onClose()} className="text-slate-700 h-12 px-5 rounded-full font-semibold hover:bg-white" data-testid="inquiry-back-btn">
            {step===1 ? "Cancel" : (<><ChevronLeft className="inline" size={18} /> Back</>)}
          </button>
          {step < 3 ? (
            <button onClick={()=>setStep(step+1)} className="btn-gold h-12 px-6 rounded-full font-semibold min-w-[140px]" data-testid="inquiry-next-btn">
              Next <ChevronRight className="inline" size={18} />
            </button>
          ) : (
            <button disabled={submitting || !form.customerName || !form.email || !form.phone} onClick={submit}
              className="btn-gold h-12 px-6 rounded-full font-semibold min-w-[180px] disabled:opacity-60" data-testid="inquiry-submit-btn">
              {submitting ? "Sending…" : "Send Inquiry"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
